import { useEffect, useState, useMemo } from "react";
import { request } from "@/api/axios";
import { RankingItem, OperatingSystem, Item } from "@/types/types";
import {
    Cpu,
    HardDrive,
    MemoryStick,
    ArrowUp,
    ArrowDown,
    Loader2,
    Monitor,
    Smartphone,
    Filter,
    Trophy
} from "lucide-react";
import { motion } from "framer-motion";
import {
    AppWindow,
    Apple,
    Terminal
} from "lucide-react";

export default function RankingComputersPage() {
    const [data, setData] = useState<RankingItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [asc, setAsc] = useState(false);

    const [selectedSecretary, setSelectedSecretary] = useState<string>("");
    const [selectedUnit, setSelectedUnit] = useState<string>("");

    useEffect(() => {
        setLoading(true);
        request<any>({
            url: "/ranking/computers",
            method: "GET",
        })
            .then((res) => {
                const list = Array.isArray(res) ? res : res?.data || res?.equipments || [];
                setData(list);
            })
            .catch(() => setData([]))
            .finally(() => setLoading(false));
    }, []);

    const secretaries = useMemo(() => {
        const set = new Set(data.map(item => item?.equipment?.local?.unit?.secretary?.name));
        return Array.from(set).sort();
    }, [data]);

    const units = useMemo(() => {
        const filteredData = selectedSecretary
            ? data.filter(item => item?.equipment?.local?.unit?.secretary?.name === selectedSecretary)
            : data;
        const set = new Set(filteredData.map(item => item?.equipment?.local?.unit?.name));
        return Array.from(set).sort();
    }, [data, selectedSecretary]);

    const filteredAndSorted = useMemo(() => {
        return data
            .filter(item => {
                const matchSec = !selectedSecretary || item?.equipment?.local?.unit?.secretary?.name === selectedSecretary;
                const matchUnit = !selectedUnit || item?.equipment?.local?.unit?.name === selectedUnit;
                return matchSec && matchUnit;
            })
            .sort((a, b) => asc ? a.score - b.score : b.score - a.score);
    }, [data, selectedSecretary, selectedUnit, asc]);

    const getSpecValue = (item: Item, keyName: string) => {
        return item.specifications?.find((s) => s.key.keyName === keyName)?.value || "";
    };

    const getOSIcon = (os?: OperatingSystem) => {
        if (!os) return <Monitor size={20} className="text-slate-500" />;
        const family = os.family.name.toLowerCase();
        const name = os.name.toLowerCase();

        if (family.includes("windows") || name.includes("windows"))
            return <AppWindow size={20} className="text-sky-400" />;
        if (family.includes("linux") || name.includes("ubuntu") || name.includes("debian"))
            return <Terminal size={20} className="text-orange-400" />;
        if (family.includes("apple") || family.includes("mac") || name.includes("macos"))
            return <Apple size={20} className="text-slate-200" />;
        if (family.includes("android"))
            return <Smartphone size={20} className="text-emerald-400" />;

        return <Monitor size={20} className="text-indigo-400" />;
    };

    const getScoreColor = (score: number) => {
        if (score >= 70) return "bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]";
        if (score >= 40) return "bg-amber-500 shadow-[0_0_10px_rgba(245,158,11,0.5)]";
        return "bg-rose-500 shadow-[0_0_10px_rgba(244,63,94,0.5)]";
    };

    if (loading) {
        return (
            <div className="page-content flex h-[50vh] flex-col items-center justify-center">
                <Loader2 className="w-10 h-10 animate-spin text-blue-500 mb-4" />
                <p className="text-slate-400 text-sm font-medium">Sincronizando ranking municipal...</p>
            </div>
        );
    }

    return (
        <div className="page-content">

            {/* SUB-HEADER */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex items-center gap-2 text-slate-500">
                    <Trophy size={18} className="text-amber-500" />
                    <span className="text-xs font-bold uppercase tracking-widest">Performance Técnica e Auditoria</span>
                </div>

                <button
                    onClick={() => setAsc(!asc)}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-800/40 border border-slate-700/50 rounded-xl text-white font-bold text-[10px] hover:bg-slate-700 transition-all active:scale-95"
                >
                    {asc ? <ArrowUp size={14} className="text-blue-400" /> : <ArrowDown size={14} className="text-blue-400" />}
                    {asc ? "PIORES PRIMEIRO" : "MELHORES PRIMEIRO"}
                </button>
            </div>

            {/* FILTROS */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 }}
                className="ui-card grid grid-cols-1 gap-4 bg-slate-900/40 p-4 backdrop-blur-sm md:grid-cols-2"
            >
                <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase text-slate-500 tracking-widest flex items-center gap-2">
                        <Filter size={12} /> Secretaria
                    </label>
                    <select
                        value={selectedSecretary}
                        onChange={(e) => { setSelectedSecretary(e.target.value); setSelectedUnit(""); }}
                        className="w-full p-2.5 bg-slate-800 border border-slate-700 rounded-xl text-sm text-slate-200 outline-none focus:ring-1 focus:ring-blue-500 appearance-none"
                    >
                        <option value="">Todas as Secretarias</option>
                        {secretaries.map(sec => <option key={sec} value={sec}>{sec}</option>)}
                    </select>
                </div>

                <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase text-slate-500 tracking-widest flex items-center gap-2">
                        <Monitor size={12} /> Unidade Operacional
                    </label>
                    <select
                        value={selectedUnit}
                        onChange={(e) => setSelectedUnit(e.target.value)}
                        className="w-full p-2.5 bg-slate-800 border border-slate-700 rounded-xl text-sm text-slate-200 outline-none focus:ring-1 focus:ring-blue-500 appearance-none"
                    >
                        <option value="">Todas as Unidades</option>
                        {units.map(unit => <option key={unit} value={unit}>{unit}</option>)}
                    </select>
                </div>
            </motion.div>

            {/* GRID DE CARDS */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6 pb-10">
                {filteredAndSorted.map((item, index) => {
                    const { equipment, score } = item;

                    const cpu = equipment.items.find(i =>
                        i.specifications?.some(s => s.key.keyName === "CPU_MODEL") ||
                        i.brand?.name?.toLowerCase().includes("intel") || i.brand?.name?.toLowerCase().includes("amd")
                    );

                    const ramItems = equipment.items.filter(i =>
                        i.specifications?.some(s => s.key.keyName.includes("RAM"))
                    );

                    const storageItems = equipment.items.filter(i =>
                        i.specifications?.some(s => s.key.keyName.includes("STORAGE"))
                    );

                    const totalRamGB = ramItems.reduce((acc, curr) => {
                        const val = getSpecValue(curr, "RAM_SIZE");
                        return acc + (parseInt(val.replace(/\D/g, "")) || 0);
                    }, 0);

                    return (
                        <motion.div
                            key={equipment.id}
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: Math.min(index * 0.035, 0.35) }}
                            whileHover={{ y: -5 }}
                            className="group relative flex flex-col rounded-3xl border border-white/5 bg-slate-900/60 p-5 shadow-2xl transition-colors duration-300 hover:border-blue-500/30"
                        >

                            <div className="absolute -top-2 -left-2 w-8 h-8 bg-blue-600 text-white rounded-xl flex items-center justify-center font-black text-xs shadow-lg">
                                {index + 1}
                            </div>

                            <div className="mb-5">
                                <div className="flex justify-between items-end mb-2">
                                    <span className="text-[9px] font-bold uppercase tracking-widest text-slate-500">Power Index</span>
                                    <span className="text-lg font-black text-white">{score.toFixed(0)}%</span>
                                </div>
                                <div className="w-full bg-slate-800/50 rounded-full h-2 p-[1px]">
                                    <div
                                        className={`${getScoreColor(score)} h-full rounded-full transition-all duration-1000 ease-out`}
                                        style={{ width: `${score}%` }}
                                    />
                                </div>
                            </div>

                            {/* IDENTIFICAÇÃO DO ATIVO (Ajuste no SO) */}
                            <div className="mb-5 pb-4 border-b border-white/5 flex justify-between items-start gap-2">
                                <div className="overflow-hidden min-w-0">
                                    <h2 className="font-bold text-white text-base truncate">ID: {equipment.patrimony || "SEM PAT."}</h2>
                                    <p className="text-[10px] text-blue-400 font-bold uppercase truncate">{equipment?.local?.unit?.name}</p>
                                    <p className="text-[10px] text-slate-500 truncate">{equipment.local.name}</p>
                                </div>

                                {/* CAIXA DO SO - Agora exibindo o nome completo */}
                                <div className="flex flex-col items-center bg-slate-800/80 p-2 rounded-xl min-w-[65px] border border-white/5">
                                    {getOSIcon(equipment.operatingSystem)}
                                    <span className="text-[8px] font-black text-slate-400 mt-1 uppercase text-center leading-tight">
                                        {equipment.operatingSystem?.name || "N/A"}
                                    </span>
                                </div>
                            </div>

                            <div className="space-y-3 flex-grow">
                                {cpu && (
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-slate-800/50 text-blue-400 rounded-lg group-hover:bg-blue-500/20 transition-colors"><Cpu size={16} /></div>
                                        <div className="flex flex-col min-w-0">
                                            <span className="text-[9px] text-slate-500 uppercase font-black tracking-tighter">Processador</span>
                                            <span className="text-xs font-bold text-slate-200 truncate">{cpu.brand?.name} {cpu.model?.name}</span>
                                        </div>
                                    </div>
                                )}

                                {ramItems.length > 0 && (
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-slate-800/50 text-emerald-400 rounded-lg group-hover:bg-emerald-500/20 transition-colors">
                                            <MemoryStick size={16} />
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-[9px] text-slate-500 uppercase font-black tracking-tighter">
                                                Memória RAM
                                            </span>
                                            {/* TIPO DA DDR ADICIONADO NO RESUMO TOTAL */}
                                            <span className="text-xs font-black text-slate-200">
                                                {totalRamGB}GB {getSpecValue(ramItems[0], "RAM_TYPE")} Total
                                            </span>

                                            <div className="flex flex-wrap gap-x-1">
                                                {ramItems.map((r, i) => {
                                                    const size = getSpecValue(r, "RAM_SIZE");
                                                    const ddr = getSpecValue(r, "RAM_TYPE");
                                                    return (
                                                        <span key={i} className="text-[9px] text-slate-500 italic font-medium">
                                                            {size}GB {ddr}{i < ramItems.length - 1 ? " +" : ""}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {storageItems.length > 0 && (
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-slate-800/50 text-amber-400 rounded-lg group-hover:bg-amber-500/20 transition-colors"><HardDrive size={16} /></div>
                                        <div className="flex flex-col min-w-0">
                                            <span className="text-[9px] text-slate-500 uppercase font-black tracking-tighter">Armazenamento</span>
                                            <span className="text-xs font-bold text-slate-200 truncate">
                                                {storageItems.map(s => `${getSpecValue(s, "STORAGE_SIZE")} ${getSpecValue(s, "STORAGE_TYPE")}`).join(" + ")}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    );
                })}
                {filteredAndSorted.length === 0 && (
                    <div className="ui-card col-span-full py-14 text-center text-sm text-slate-500">
                        Nenhum computador encontrado para os filtros selecionados.
                    </div>
                )}
            </div>
        </div>
    );
}
